import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, LogOut, Menu, Settings, User, X } from 'lucide-react';
import { navigationItems, images } from '../data/mockData';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import LoginModal from './LoginModal';

const primaryNavigation = navigationItems.filter((item) =>
  ['BOOK NOW', 'PRACTITIONERS', 'EXPERIENCES'].includes(item.name)
);
const exploreNavigation = navigationItems.filter((item) =>
  ['MEMBERSHIP', 'COMMUNITY', 'HOW IT WORKS', 'ABOUT US', 'FAQ', 'REVIEWS'].includes(item.name)
);
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isExploreMenuOpen, setIsExploreMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [landingPageLinks, setLandingPageLinks] = useState([]);
  const { user, logout, loading } = useAuth();

  useEffect(() => {
    let active = true;
    const loadLandingPageLinks = () => {
      fetch(`${API_BASE}/api/landing-pages/menu`, { cache: 'no-store' })
        .then(async (response) => {
          if (!response.ok) throw new Error('Could not load the landing-page menu items.');
          return response.json();
        })
        .then((items) => {
          if (active) setLandingPageLinks(Array.isArray(items) ? items : []);
        })
        .catch((error) => console.error(error));
    };
    loadLandingPageLinks();
    window.addEventListener('landing-pages-updated', loadLandingPageLinks);
    return () => {
      active = false;
      window.removeEventListener('landing-pages-updated', loadLandingPageLinks);
    };
  }, []);

  const handleExternalLink = (href) => {
    if (href.startsWith('http')) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  };

  const handleLogin = () => {
    setIsLoginModalOpen(true);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    setIsUserMenuOpen(false);
  };

  const getDashboardLink = () => {
    if (!user) return '/';
    if (user.role === 'admin') return '/admin-dashboard';
    if (user.role === 'practitioner') return '/practitioner-dashboard';
    return '/member-dashboard';
  };

  const closeExplore = () => setIsExploreMenuOpen(false);

  const renderNavigationItem = (item, { mobile = false } = {}) => {
    const className = mobile
      ? 'block w-full px-3 py-2.5 text-left text-sm font-medium uppercase tracking-wide text-white transition-colors hover:text-yellow-300'
      : 'px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:text-yellow-300';

    if (item.href.startsWith('http')) {
      return (
        <button
          key={item.name}
          onClick={() => {
            handleExternalLink(item.href);
            if (mobile) setIsMobileMenuOpen(false);
          }}
          className={className}
        >
          {item.name}
        </button>
      );
    }

    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={() => {
          closeExplore();
          if (mobile) setIsMobileMenuOpen(false);
        }}
        className={className}
      >
        {item.name}
      </Link>
    );
  };

  return (
    <header className="fixed top-0 z-50 w-full border-b border-white/10 bg-[#09050f]/90 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-[76px] items-center justify-between gap-6">
          <Link
            to="/"
            className="group flex min-w-0 shrink-0 items-center gap-3"
            aria-label="The Wisdom Network home"
          >
            <img
              src={images.wisdomMark}
              alt=""
              className="h-11 w-11 rounded-full object-cover ring-1 ring-yellow-300/40 transition-transform duration-300 group-hover:scale-105"
              fetchPriority="high"
              decoding="async"
            />
            <span className="hidden text-lg font-semibold tracking-[0.16em] text-transparent bg-gradient-to-r from-yellow-200 via-yellow-400 to-amber-200 bg-clip-text sm:block md:text-xl">
              THE WISDOM NETWORK
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Main navigation">
            {primaryNavigation.map((item) => renderNavigationItem(item))}
            <div className="relative">
              <button
                type="button"
                aria-expanded={isExploreMenuOpen}
                onClick={() => setIsExploreMenuOpen((open) => !open)}
                className="flex items-center gap-1 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:text-yellow-300"
              >
                More
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isExploreMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {isExploreMenuOpen && (
                <div className="absolute right-0 top-full mt-3 max-h-[75vh] w-64 overflow-y-auto rounded-xl border border-purple-700/50 bg-[#12091d]/95 p-2 shadow-2xl backdrop-blur-md">
                  {exploreNavigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={closeExplore}
                      className="block rounded-lg px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-purple-800/60 hover:text-yellow-300"
                    >
                      {item.name}
                    </Link>
                  ))}
                  {landingPageLinks.length > 0 && (
                    <>
                      <div className="my-2 border-t border-purple-700/60" />
                      {landingPageLinks.map((item) => (
                        <Link
                          key={item.slug}
                          to={`/offers/${encodeURIComponent(item.slug)}`}
                          onClick={closeExplore}
                          className="block rounded-lg px-3 py-2.5 text-xs font-semibold tracking-[0.08em] text-yellow-100 transition-colors hover:bg-purple-800/60 hover:text-yellow-300"
                        >
                          {item.title}
                        </Link>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </nav>

          <div className="hidden shrink-0 items-center md:flex">
            {loading ? (
              <div className="px-3 text-xs text-white/70">Loading...</div>
            ) : user ? (
              <div className="relative">
                <Button
                  onClick={() => setIsUserMenuOpen((open) => !open)}
                  variant="ghost"
                  className="flex items-center gap-2 px-3 text-white hover:bg-purple-700/50 hover:text-yellow-300"
                >
                  <User className="h-5 w-5" />
                  <span className="max-w-[120px] truncate text-sm font-medium">{user.name}</span>
                </Button>
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 rounded-lg border border-purple-700/50 bg-black/95 shadow-lg backdrop-blur-md">
                    <Link
                      to={getDashboardLink()}
                      className="flex items-center px-4 py-2.5 text-sm text-white hover:bg-purple-700/50 hover:text-yellow-300"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Settings className="mr-3 h-4 w-4" />
                      Dashboard
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center px-4 py-2.5 text-sm text-white hover:bg-purple-700/50 hover:text-yellow-300"
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Button
                type="button"
                onClick={handleLogin}
                className="rounded-lg bg-purple-600 px-5 py-2 text-sm font-bold text-white shadow-lg transition-all duration-300 hover:bg-purple-700"
              >
                Admin Login
              </Button>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((open) => !open)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            className="inline-flex items-center justify-center rounded-md bg-purple-800/50 p-2 text-yellow-300 transition-all hover:bg-purple-700/70 hover:text-white md:hidden"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="border-t border-white/10 py-3 md:hidden">
            <div className="rounded-xl bg-black/90 p-2 backdrop-blur-md">
              {primaryNavigation.map((item) => renderNavigationItem(item, { mobile: true }))}
              <div className="mt-2 border-t border-purple-700/50 px-3 pt-3">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-yellow-300/80">
                  More
                </p>
                {exploreNavigation.map((item) => renderNavigationItem(item, { mobile: true }))}
                {landingPageLinks.length > 0 && (
                  <div className="mt-2 border-t border-purple-700/50 px-3 pt-3">
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-yellow-300/80">
                      Offers
                    </p>
                    {landingPageLinks.map((item) => (
                      <Link
                        key={item.slug}
                        to={`/offers/${encodeURIComponent(item.slug)}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="block w-full px-3 py-2.5 text-left text-sm font-medium text-white transition-colors hover:text-yellow-300"
                      >
                        {item.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-2 border-t border-purple-700/50 pt-2">
                {user ? (
                  <>
                    <Link
                      to={getDashboardLink()}
                      className="block w-full px-3 py-2.5 text-sm font-medium text-white hover:text-yellow-300"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Dashboard ({user.name})
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full px-3 py-2.5 text-left text-sm font-medium text-white hover:text-yellow-300"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleLogin}
                    className="block w-full px-3 py-2.5 text-left text-sm font-medium text-white hover:text-yellow-300"
                  >
                    Admin Login
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        userType="client"
      />
    </header>
  );
};

export default Header;